import { useAppStatus } from "../../lib/app-status";
import { MaintenanceScreen } from "../settings/MaintenanceScreen";
import { Splash } from "../Splash";
import { useTranslation } from "react-i18next";
import { useSearch } from "@tanstack/react-router";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Button } from "../ui/button";
import { HomeAccount } from "./HomeAccount";
import { EmptyRooms } from "./EmptyRooms";
import { RoomCard } from "./RoomCard";
import { CreateRoom } from "./CreateRoom";
import { HomeNotice } from "./HomeNotice";
import { useRoomDirectory } from "../../lib/rooms";
import { useAuth } from "../../auth/auth";
import { usePrefetchCallWhenIdle } from "../../room/active-call";

export function Home() {
  const auth = useAuth();
  const status = useAppStatus();
  const { moved, ended } = useSearch({ from: "/" });
  const { t } = useTranslation();
  const { rooms, error, reconnect } = useRoomDirectory();
  usePrefetchCallWhenIdle();
  if (status.maintenance) return <MaintenanceScreen message={status.data?.maintenance.message} />;
  if (status.isPending) return <Splash />;
  return (
    <main className="flex h-full flex-col bg-background text-foreground [--home-content-gap:calc(var(--spacing)*12)] max-[30rem]:[--home-content-gap:calc(var(--spacing)*9)]">
      <header className="flex-none px-6 pt-10 pb-(--home-content-gap) text-center max-[45rem]:pt-9 max-[30rem]:px-4.5 md:pt-22.5">
        <h1 className="flex flex-wrap items-center justify-center gap-3.75 font-heading text-4xl leading-tight font-medium tracking-tighter max-[30rem]:gap-2 md:text-7xl">
          <span>{t("home.hello")}</span> <HomeAccount user={auth.user} onSignOut={auth.signOut} />
          <span className="-ml-3.75 max-[30rem]:-ml-2">.</span>
        </h1>
      </header>
      <HomeNotice moved={moved} ended={ended} />
      <Tabs defaultValue="recent">
        <TabsList aria-label={t("home.rooms")}>
          <TabsTrigger value="recent">{t("home.recent")}</TabsTrigger>
          <TabsTrigger value="public">{t("home.public")}</TabsTrigger>
        </TabsList>
        {error ? (
          <div role="status" className="px-3 pb-2.5 text-center text-sm text-muted-foreground">
            {t(error === "reconnecting" ? "home.reconnecting" : `home.errors.${error}`, {
              defaultValue: t("home.reconnecting"),
            })}
            <Button
              variant="ghost"
              onClick={() =>
                error === "authentication-required" ? window.location.reload() : reconnect()
              }
            >
              {t("home.retry")}
            </Button>
          </div>
        ) : null}
        {["recent", "public"].map((tab) => {
          const visible = (rooms ?? []).filter((room) =>
            tab === "recent" ? room.visited : room.visibility === "public",
          );
          return (
            <TabsContent
              key={tab}
              value={tab}
              className="min-h-0 flex-1 [scrollbar-width:thin] [scrollbar-color:var(--color-zinc-600)_transparent] overflow-y-auto px-7 py-1.5 data-[state=active]:animate-[room-tab-enter_200ms_ease-out] motion-reduce:data-[state=active]:animate-none max-[30rem]:px-5"
            >
              {!rooms ? (
                <p role="status" className="my-3.5 p-4 text-center text-sm text-muted-foreground">
                  {t("home.loading")}
                </p>
              ) : visible.length ? (
                <div className="mx-auto grid w-full max-w-260 grid-cols-4 gap-4 max-[60rem]:grid-cols-3 max-[45rem]:grid-cols-2 max-[30rem]:grid-cols-1">
                  {visible.map((room) => (
                    <RoomCard room={room} key={room.id} showVisibility={tab !== "public"} />
                  ))}
                </div>
              ) : (
                <EmptyRooms recent={tab === "recent"} />
              )}
            </TabsContent>
          );
        })}
      </Tabs>
      <footer className="flex flex-none justify-center bg-background px-6 pt-(--home-content-gap) pb-10 max-[30rem]:pb-[max(1.25rem,env(safe-area-inset-bottom))]">
        <CreateRoom />
      </footer>
    </main>
  );
}
